<script setup>
import { formatTemp, getMaskImageStyle } from '@@/utils/CommonUtils';
import { scrollActiveElementIntoView } from '@@/utils/Components/ComponentUtils';
import { useMetaStore } from '@@/stores/Meta';
import { useUserStore } from '@@/stores/User';
import { useCssModule, useTemplateRef } from 'vue';

const props = defineProps({
  canSelectForecastDay: {
    type: Boolean,
    default: false,
  },
  forecastDaily: {
    type: Array,
    default: null,
  },
  forecastDay: {
    type: Number,
    default: null,
  },
  inLocationWeatherPage: {
    type: Boolean,
    default: false,
  },
  inMapCard: {
    type: Boolean,
    default: false,
  },
  isMyLocation: {
    type: Boolean,
    default: false,
  },
});

const $emit = defineEmits(['click-forecast-day']);
const $style = useCssModule();

const metaStore = useMetaStore();
const userStore = useUserStore();

const table = useTemplateRef('table');
const tableCells = useTemplateRef('tableCells');

const canShowAuthContent = computed(() => userStore.canShowAuthContent);
const isAllAccess = computed(() => userStore.isAllAccess);
const units = computed(() => userStore.preferences.units);

const iconClass = 'tw-inline-block tw-h-4 lg:tw-h-5 tw-w-4 lg:tw-w-5';

const alertIconClass = computed(() => {
  const className = `${iconClass} last:tw-mr-0`;

  if (props.inMapCard) {
    return `${className} tw-mr-px lg:tw-mr-0.5`;
  }

  return `${className} tw-mr-0.5`;
});

const conditionsIconClass = 'tw-block tw-mx-auto tw--mt-px tw-h-5 lg:tw-h-6 tw-w-5 lg:tw-w-6';

/**
 * Filter the day's alerts so that only those with an alert or level that should show a badge
 * are displayed in the table.
 */
const getVisibleAlerts = (day) => {
  if (day?.alerts?.length) {
    return day.alerts.reduce((alerts, alert) => {
      const level = metaStore.getLevelForAlert(alert);

      if (alert.show_badge ?? level.show_badge) {
        alerts.push(alert);
      }

      return alerts;
    }, []);
  }

  return null;
};

const forecast = computed(() => {
  if (props.forecastDaily) {
    return props.forecastDaily.map((day) => ({
      ...day,
      visibleAlerts: getVisibleAlerts(day),
    }));
  }

  return [];
});

const hasClickedForecastDay = ref(false);
const hideRisk = computed(() => props.isMyLocation ? false : !isAllAccess.value);

const tempValueClass = computed(() => {
  const className = 'tw-whitespace-nowrap';
  return props.inMapCard ? className : `${className} lg:tw-w-1/2 lg:tw-text-left lg:tw-pl-1`;
});

const highTempClass = computed(() => `tw-font-bold ${tempValueClass.value}`);
const isTenDayForecast = computed(() => props.forecastDaily?.length === 10);
const lowTempClass = computed(() => `tw-font-normal ${tempValueClass.value}`);

const tableClass = computed(() => ([
  'tw-border-collapse',
  'tw-table-fixed',
  isTenDayForecast.value ? $style.table10DayForecast : 'tw-w-full',
]));

const tempLabelClass = computed(() => (props.inMapCard || isTenDayForecast.value)
  ? 'tw-hidden'
  : 'tw-hidden lg:tw-inline-block tw-w-1/2 tw-text-right text-light-color tw-pr-1');

const tempRowClass = 'tw-flex tw-justify-center tw-items-center';

const forecastDayClick = (index) => {
  hasClickedForecastDay.value = true;
  $emit('click-forecast-day', index);
};

const handleFormatTemp = (temp) => formatTemp(temp, units.value);

const getAlertStyle = (alert) => {
  const level = metaStore.getLevelForAlert(alert);

  // Get the icon and color from the alert if present. Otherwise get them from the level.
  const iconUrl = alert.icon_url || level.icon_url;
  const backgroundColor = alert.color_primary || level.color_primary;

  return {
    backgroundColor,
    ...getMaskImageStyle(iconUrl),
  };
};

const getConditionsStyle = (index) => {
  const conditionsIconUrl = forecast.value[index].conditions_icon_url;
  return {
    ...getMaskImageStyle(conditionsIconUrl),
  };
};

const getForecastDayClass = (index) => {
  const classNames = [
    props.isTenDayForecast ? null : 'tw-w-1/5',
    props.inMapCard ? 'tw-px-1 tw-py-2 lg:tw-px-2' : 'tw-p-2',
    'border-color',
    'tw-border-r',
    'last:tw-border-r-0',
    'tw-text-center',
    'tw-align-top',
    typeof props.forecastDay === 'number' || props.canSelectForecastDay ? 'tw-cursor-pointer' : null,
  ];

  if (props.forecastDay === null) {
    classNames.push('card-background-color');
  }
  else if (index === props.forecastDay) {
    classNames.push($style.selectedForecastDay);
  }
  else {
    classNames.push($style.selectableForecastDay);
  }

  return classNames.filter((className) => className !== null);
};

watch(() => props.forecastDay, () => {
  if (props.inLocationWeatherPage) {
    const handleScroll = () => {
      const activeElement = tableCells.value[props.forecastDay];

      scrollActiveElementIntoView({
        parent: table.value,
        activeElement,
      });
    };

    if (hasClickedForecastDay.value) {
      handleScroll();
      hasClickedForecastDay.value = false;
    }
    else {
      // HACK: Handle the scroll a short time after the forecast day changes if the user hasn't
      // clicked on a forecast day to allow the location weather page to scroll down to the 10-Day
      // Weather Forecast card _before_ scrolling the active forecast day into view. Scrolling the
      // active forecast day into view when the page is already scrolling down to the 10-Day
      // Forecast card would interrupt that scroll.
      window.setTimeout(handleScroll, 1000);
    }
  }
});
</script>

<template>
  <table
    v-if="forecast.length"
    ref="table"
    :class="tableClass"
  >
    <tbody>
      <tr>
        <td
          v-for="(day, index) in forecast"
          :key="day.display_at_local_label"
          ref="tableCells"
          :class="getForecastDayClass(index)"
          @click.prevent.stop="forecastDayClick(index)"
        >
          <div class="tw-border-b tw-mb-1 tw-pb-1 border-color">
            <strong
              class="tw-uppercase tw-text-sm tw-font-bold tw-whitespace-nowrap"
            >
              {{ day.display_at_local_label }}
            </strong>
          </div>
          <div
            v-if="!canShowAuthContent"
            class="tw-h-28"
          >
            &nbsp;
          </div>
          <div v-else>
            <div class="tw-border-b tw-pb-1 border-color">
              <div :class="tempRowClass">
                <small :class="tempLabelClass">
                  High
                </small>
                <strong :class="highTempClass">
                  {{ handleFormatTemp(day.temp_max) }}
                </strong>
              </div>
              <div :class="tempRowClass">
                <small :class="tempLabelClass">
                  Low
                </small>
                <strong :class="lowTempClass">
                  {{ handleFormatTemp(day.temp_min) }}
                </strong>
              </div>
            </div>
            <div class="tw-py-2">
              <div
                v-if="hideRisk"
              >
                <font-awesome-icon
                  :class="[iconClass, 'tw-fill-current all-access-color']"
                  icon="lock"
                />
              </div>
              <div
                v-else-if="forecast[index].visibleAlerts && forecast[index].visibleAlerts.length"
                class="tw-whitespace-nowrap"
              >
                <span
                  v-for="alert in forecast[index].visibleAlerts"
                  :key="alert.alert_id"
                  :class="alertIconClass"
                  :style="getAlertStyle(alert)"
                />
              </div>
              <div v-else>
                <span
                  :class="[conditionsIconClass, 'text-regular-background-color']"
                  :style="getConditionsStyle(index)"
                />
              </div>
            </div>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style module>
.table10DayForecast {
  width: 48rem;
}

@media (min-width: 768px) {
  .table10DayForecast {
    width: 62rem;
  }
}

@media (min-width: 992px) {
  .table10DayForecast {
    width: 100%;
  }
}

.table10DayForecast td {
  width: 10%;
}

.selectedForecastDay {
  background-color: var(--card-background);
}

.selectableForecastDay {
  background-color: var(--view-background);
}
</style>
